import { IBaseEntityResponse, IBaseEntityType } from '../base';
import {
  IGridFlatInventoryItemVenue,
  ISite,
  ObjectId,
  IHistoricalQuantitiesTransfer,
  ENTITY_TYPES,
  TRANSFER_TYPE,
  IEvent,
  REPLENISH_TYPE_VALUES,
  ISiteForFromGridItem,
  IInventoryItem,
  REPLENISH_APPLY_ITEMS_SCOPE,
  TRANSFER_TYPE_VALUE,
  IGridFlatInventoryItemVenueTotal,
} from '@shared/types';
import {
  GeneralLedgerAccount,
  InventoryItemVenue,
} from '@geniuspos/xenial-data-contracts/types/dm-entities';
import { ITreeData } from '@shared/types/common/tree.type';
import { DecimalNumber } from '@app/shared/common';
import { TRANSFER_STATUS_VALUE } from '@shared/types/enums/transfer-status.enum';
import { IPdfTools, PdfFiledValue } from '@app/shared/common/make-pdf/v2';
import { IStandWorkSheetItem } from '@shared/types/entities/stand-worksheet';
import { XuxEditGridComponent } from '@app/components/xux/xux-edit-grid/xux-edit-grid-widget.component';
import {
  InventoryItemVenueMeasure,
  Threshold,
  Cost,
} from '@geniuspos/xenial-data-contracts/types/dm-entities/inventory-item-venue';

export enum TemplateSettingType {
  EMPTY = 'empty',
  VEHICLE = 'vehicle',
}

type QohType = DecimalNumber | number | string;

export type FromAndToSiteId = [string, string];

export interface ITransfer extends IBaseEntityType {
  inventory_items: IGridFlatInventoryItemVenue[];
  gl_account_name: string;
  count: DecimalNumber;
  total_cost: DecimalNumber;

  virtualTotalStatus?: string;
}

export interface ITransferData extends IBaseEntityType {
  _id: string;
  site_id: string;
  site_id_to: string;
  transaction_date: string;
  status: TRANSFER_STATUS_VALUE;
  transfer_type: TRANSFER_TYPE;
  total_cost: DecimalNumber;
  updated_at: string;
  site_department_code_to: string;
  transfer_group_entity_id?: ObjectId;

  /**
   * is visible void btn,
   * only used in ui
   */
  visibleVoidBtn?: boolean;
}

export interface ITransferFormFieldPath {
  isChargeable?: string;
  type?: string;
  status?: string;
  transferType?: string;
  eventId?: string;
  siteId?: string;
  siteIdTo?: string;
  transactionDate?: string;
  inventoryItemVenues?: string;
  departmentCodeTo?: string;
  glAccountName?: string;
  initialCount?: string;
  count?: string;
  measureEntityId?: string;
  measureFactorValue?: string;
  itemCost?: string;
  totalCost?: string;
  storeNumber?: string;
  storeNumberTo?: string;
  siteName?: string;
  siteNameTo?: string;
  quantityReversed?: string;
  reversedCost?: string;
  receivedTotalCost?: string;
  category?: string;
  transferGroupName?: string;
  qohFrom?: string;
  par_qty?: string;
  source?: string;
  is_send?: string;

  // virtual
  virtualTotals?: string;
  virtualInventoryItemVenues?: string;
  /**
   * this is full date time for transaction date
   * you can set this if you want to keep full transaction date time(like during transfer now)
   */
  virtualTransactionDate?: string;
  virtualTransferDestinations?: string;
  virtualGrandTotals?: string;
  virtualSelectedEvent?: string;
  virtualReallyChangedTransfers?: string;
  virtualFromSite?: string;
  virtualToSite?: string;

  /**
   * for cache autoPopulate data and status
   */
  virtualAutoPopulate?: string;
  isMeasureChanged?: string;
}

export interface ITransferItem {
  name?: string;
  gl_account?: Partial<GeneralLedgerAccount>;
  qoh_to?: DecimalNumber;
  qoh_from?: DecimalNumber;
  transfer_unit?: string;
  upc_codes?: string[];
  item_entity_id?: string;
}

export interface IGrandTotal {
  received_cost: number;
  request_cost: number;
  reversed_cost: number;
}

/**
 * key is site id to, value is transfer inventory item list
 */
export type ReallyChangedTransfers = Record<
  string,
  {
    items: Pick<ITransferData, 'entity_id'> & Partial<ITransferData>[];
    originItems?: ITransferData[];
    grand_total: IGrandTotal;
  }
>;

export type ITransferErrorMapper = Record<
  string,
  { error: boolean; _id: string }
>;

export interface IQuantityOnHandRequestParams {
  items: IQuantityOnHandItem[];
  top?: number;
  skip?: number;
  select?: string;
}

export interface IQuantityOnHandItem {
  site_id: ObjectId;
  inventory_item_entity_ids: string[];
}

export interface IQuantityOnHandResponse {
  current_page: number;
  items: IQohInventoryItemVenue[];
  page_count: number;
  page_size: number;
  total: number;
}

export interface IQOHItem {
  count: number;
  entity_id: ObjectId;
  inventory_item_entity_id: ObjectId;
  site_id: ObjectId;
  _id: ObjectId;
}

export interface IQohInventoryItemVenue
  extends InventoryItemVenue,
    MultiplyCountUnit {
  count: number;
  inventory_item_entity_id: string;
  item_entity_id?: string;
  manual_count_date: string;
  site_id: string;
  measure_entity_id?: string;
  qoh_to?: QohType;
  qoh_from?: QohType;
  pending_qty_from?: string;
  pending_qty_to?: string;
  item_cost?: DecimalNumber;
  total_cost?: DecimalNumber;
  costs?: Cost[];
  origin_measures?: InventoryItemVenueMeasure[];
  preservedInitialValues?: InitialFactorAndCountValue;
  error?: string[];
  iconsInfo?: IconsColumnInfo;
  initial_count?: DecimalNumber | string | number;
  gl_account_name?: string;
  major_category_name?: string;
  minor_category_name?: string;
  measure_description?: string;
  measure_factor_value?: number;
  qohTo?: string[];
  qohFrom: string[];
  is_chargeable?: boolean;
  qohItems?: IQOHItem[];
  cost?: number;
  item_base_unit?: string;

  /**
   * is duplicated item in transfer
   */
  is_duplicated?: boolean;
}

export interface MultiplyCountUnit {
  base_unit?: InventoryItemVenueMeasure | string;
  default_transfer_unit?: InventoryItemVenueMeasure | string;
  default_ordering_unit?: InventoryItemVenueMeasure | string;
  uom?: InventoryItemVenueMeasure | string;
}

export interface IconsColumnInfo {
  dmWarning?: boolean;
  // quantityRequestThreshold will be number when dm set threshold and it will be false when dm set no threshold.
  thresholdMaxValue?: number | boolean;
  thresholdMinValue?: number | boolean;
  hiddenThresholdWarning: boolean;
  thresholdMeasure?: InventoryItemVenueMeasure;
}

export interface ApiInfo {
  info: {
    [key: string]: string;
  };
}

export interface ISelectSiteAndEvents {
  site_id: string;
  event_type_id: string;
  event_ids: string[];
  url: string;
}

export interface IPageJumpRouteState {
  selectedSite: SelectedSite;
  storeForm: ITransfer;

  isFormDirty?: boolean;

  createSuccess?: boolean;
  needBeforePageLoadPopup?: boolean;
  action_type?: string;
  historicalInventoryItems?: IHistoricalQuantitiesTransfer[];
  site_id_to?: string;
  site_name_to?: string;
  store_number_to?: string;
  isWarehouse?: boolean;
  historical_create_transfer?: boolean;
  entityType?: ENTITY_TYPES;
  [key: string]: any;
}

export interface SelectedSite extends ISite {
  id: ObjectId;
  status: string;
  inventory_items: ITreeData[];
  site_id_to: string;
  internal_number?: string;
  site_name_to?: string;
}
export interface InitialFactorAndCountValue {
  factorValue: number;
  receivedFactorValue: number;
  count?: string;
  initial_count?: string;
}
export interface ITransferDestination extends ISite {
  status: string;
  inventory_items?: ITreeData[];
  disabled?: boolean;
  // #TODO remove this field when remove transfer
  totalCost?: number;
  replenish_percent: string | number;
  noAvailablePAR?: boolean;
  skipReplenish?: boolean;

  site_department_code_to?: string;
  request_cost?: number;
  received_cost?: number;
  reversed_cost?: number;
  site_id_to?: string;
  _id?: string;
  isChanged?: boolean;
  site_id?: string;
  reallyChangedItems?: Pick<ITransferData, 'entity_id'> &
    Partial<ITransferData>[];
}

export interface ITransferDestinationOption extends ITransferDestination {
  status: string;
  inventory_items?: ITreeData[];
  // #TODO remove this field when remove transfer
  totalCost?: number;
  replenish_percent: string | number;
  noAvailablePAR?: boolean;
  skipReplenish?: boolean;

  site_department_code_to?: string;
  request_cost?: number;
  received_cost?: number;
  reversed_cost?: number;
  site_id_to?: string;
  _id?: string;

  selected: boolean;
  value: string;
}

export interface ITransferGrandTotal {
  department_code: string;

  request_cost?: number | DecimalNumber;
  received_cost?: number | DecimalNumber;
  reversed_cost?: number | DecimalNumber;
}

// #TODO remove this field when remove transfer
export interface ITransferDepartmentTotal {
  departmentCode: string;
  totalCost: number;
}

export interface ITransferDepartmentTotalMap {
  [key: string]: number;
}

export interface ITransferReplenishPercentDictionary {
  [key: string]: string;
}

export interface ITransferReplenishDestinationsParams {
  destinations: ITransferDestination[];
  inventoryItemGroup: IBaseEntityResponse<IInventoryItem>;
  replenishType: REPLENISH_TYPE_VALUES;
  siteFromId: string;
  dictionary?: ITransferReplenishPercentDictionary;
  replenishItemOption?: REPLENISH_APPLY_ITEMS_SCOPE;
}

export interface TotalHeader {
  text: string;
  textAlign?: TextAlign;
}

export enum TextAlign {
  LEFT = 'left',
  CENTER = 'center',
  RIGHT = 'right',
}

export interface TotalCountData {
  initialTotalCount: DecimalNumber;
  totalAllCost: DecimalNumber;
  totalCount: DecimalNumber;
}

export interface ITransferReplenishGettingParams {
  formFieldPath: ITransferFormFieldPath;
  replenishType: REPLENISH_TYPE_VALUES;
  isOutsideOfEventTransfer: boolean;
  selectedEvent: IEvent;
  items: IQohInventoryItemVenue[];
  replenishPercent: number;
  replenishItemOption?: REPLENISH_APPLY_ITEMS_SCOPE;
}

export interface ITransferReplenishCalculationFormulaParams {
  replenishQuantity: DecimalNumber;
  thresholdsMeasureFactorValue: DecimalNumber;
  qohTo: DecimalNumber;
  pendingQtyTo: DecimalNumber;
  selectedMeasureFactorValue: DecimalNumber;
  replenishPar: DecimalNumber;
  replenishMin: DecimalNumber;
}

// for auto restock
export interface ITransferReplenishCalculationFormulaParamsForRestock {
  standWorksheetItems: IStandWorkSheetItem[];
  lastEventOfDestination: Partial<IEvent>;
  replenishPercent: DecimalNumber;
  thresholdsMeasureFactorValue: DecimalNumber;
  selectedMeasureFactorValue: DecimalNumber;
  itemThreshold: Threshold;
  standWorksheetItemsMap: IStandWorkSheetItem;
  item: IQohInventoryItemVenue;
}

export interface ITransferAdjustReplenishOptions {
  replenishPar: DecimalNumber;
  replenishmentValue: DecimalNumber;
  selectedMeasureFactorValue: DecimalNumber;
  thresholdsMeasureFactorValue: DecimalNumber;
  valueOfParticipateInCalculations: DecimalNumber;
}

export interface CompositionParams<T = any> {
  value: PdfFiledValue<T>;
  tools: IPdfTools;
  pendingQty: DecimalNumber;
  measureFactorValue: number;
}

export interface IBulkReplenishOption {
  items: ISiteForFromGridItem[];
  replenishType?: REPLENISH_TYPE_VALUES;
  itemOption: REPLENISH_APPLY_ITEMS_SCOPE;
}

export interface IAutoRestockReplenishParams {
  inventoryItems: IQohInventoryItemVenue[];
  standWorksheetItems: IStandWorkSheetItem[];
  selectedEvent: IEvent;
  lastEventOfDestination: Partial<IEvent>;
  itemOption: IBulkReplenishOption['itemOption'];
  replenishPercent: DecimalNumber;
  standWorksheetItemsMap: IStandWorkSheetItem;
}

export interface ITransferGroupInfo {
  transfer_type: TRANSFER_TYPE_VALUE;
  name: string;
  event_id: ObjectId;
  site_id: ObjectId;
  transaction_date: string;
  transfer_items: ITransferData[];
}

export interface ITransferGroupFormFieldPath {
  transferType?: string;
  name?: string;
  eventId?: string;
  eventName?: string;
  siteId?: string;
  siteName?: string;
  storeNumber?: string;
  transactionDate?: string;
  /**
   * this is full date time for transaction date
   * you can set this if you want to keep full transaction date time(like during transfer now)
   */
  virtualTransactionDate?: string;

  virtualTransferItems?: string;
  virtualGrandTotals?: string;
  virtualSelectedEvent?: string;
  initialCount?: string;
  count?: string;
  siteIdTo?: string;
  measureFactorValue?: string;
  measureEntityId?: string;
  inventoryItemVenues?: string;
  transferStatus?: string;
  status?: string;
  virtualInventoryItemVenues?: string;
  totalCost?: string;
  qohFrom?: string;

  virtualReallyChangedTransfers?: string;
  transferGroupType?: string;
  templateId?: string;
  siteIdsTo?: string;
  virtualTemplateId?: string;
  virtualSiteIdsTo?: string;
}

export interface IDataOptions {
  xuxGrid?: XuxEditGridComponent;
  docEvent?: IEvent;
  isFromDetail?: boolean;
}

export interface NotAssignedSites {
  fromSiteName?: string;
  toSiteName?: string;
}

export interface DuplicateError {
  notAssignedSites: NotAssignedSites;
  isPrimaryWarehouseError: boolean;
  fromSiteName?: string;
  toSiteName?: string;
}

export interface MergeRowsConfig {
  fieldsToSum: string[];
  defaultValues: IGridFlatInventoryItemVenueTotal;
}
